import { Injectable } from '@angular/core';
import { Issue } from './issue.dto';
import { Observable, of, startWith } from 'rxjs';
import { ChangeTyped, SearchResult } from '@damofire/data-commons';
import { TypedDesQuery } from '../des.dto';
import { ObservableNoteService } from '../note/note-observable.service';
import { ObservableNoteableService } from '../note/noteable-observable.service';
import { Note, Noteable } from '../note/note.dto';

export const ISSUE_SEARCH_FIELDS = [
  'warehouseIssueId',
  'action.descriptionEn',
  'action.descriptionFr',
  'component.descriptionEn',
  'component.descriptionFr',
  'description.descriptionEn',
  'description.descriptionFr',
  'status.descriptionEn',
  'status.descriptionFr',
  'asset.description',
  'inspection.name',
  'riskLevel.descriptionEn',
  'riskLevel.descriptionFr',
  'notes[text]',
];

@Injectable()
export abstract class ObservableIssueService extends ObservableNoteableService<Issue> {
  protected readonly SEARCH_FIELDS = ISSUE_SEARCH_FIELDS;
  protected readonly notesFilterFn = (note: Note, issue: Noteable): boolean => issue._id === note.issue?._id;

  protected constructor(protected readonly observableNoteService: ObservableNoteService) {
    super(observableNoteService);
  }

  protected abstract observeById(division: string, id: string): Observable<ChangeTyped<Issue>>;
  abstract observeByAssets(divisionId: string, ids: number[]): Observable<ChangeTyped<Issue>[]>;
  protected abstract observeOpen(id: string, section?: string): Observable<ChangeTyped<Issue>[]>;
  protected abstract observeByInspection(divisionId: string, id: string): Observable<ChangeTyped<Issue>[]>;
  abstract observeByAsset(divisionId: string, assetId: number, inspectionId?: string): Observable<ChangeTyped<Issue>[]>;
  abstract observeOpenByAsset(divisionId: string, assetId: number): Observable<ChangeTyped<Issue>[]>;
  protected abstract noteableSearch(id: string, sQuery: TypedDesQuery): Observable<SearchResult<Issue>>;

  search(divisionId: string, sQuery: TypedDesQuery, forceNoteInspectionId?: string): Observable<SearchResult<Issue>> {
    return super.searchByInspection(divisionId, sQuery, forceNoteInspectionId);
  }

  observeOpenByWarehouse(divisionId: string, inspectionId?: string, section?: string): Observable<ChangeTyped<Issue>[]> {
    const noteablesSub = this.observeOpen(divisionId, section);
    let noteSub = of([]);
    if (inspectionId) {
      noteSub = this.observableNoteService.observeNotes(divisionId, inspectionId).pipe(startWith([]));
    }
    return this.combineNoteablesNotes(noteSub, noteablesSub);
  }

  observeInspection(divisionId: string, inspectionId: string): Observable<ChangeTyped<Issue>[]> {
    const noteablesSub = this.observeByInspection(divisionId, inspectionId);
    const noteSub = this.observableNoteService.observeNotes(divisionId, inspectionId).pipe(startWith([]));
    return this.combineNoteablesNotes(noteSub, noteablesSub);
  }

  observeIssue(divisionId: string, issueId: string): Observable<ChangeTyped<Issue>> {
    const noteableSub = this.observeById(divisionId, issueId);
    const noteSub = this.observableNoteService.observeIssueNotes(divisionId, issueId).pipe(startWith([]));
    return this.combineNoteableNotes(noteSub, noteableSub);
  }
}
